<template>
  <div>
    <div class="d-flex justify-center mt-4">
      <v-btn color="primary" outlined @click="openDialog">
        {{ $t("riskAssessment.chooseRiskFromTemplate") }}
      </v-btn>
    </div>

    <v-dialog v-model="dialog" max-width="800" persistent scrollable>
      <v-card>
        <v-card-title class="d-flex align-center flex-nowrap">
          <v-btn v-if="step !== 'folders'" icon class="mr-2" @click="goBack">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="text-truncate">{{ dialogTitle }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-2">
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="my-4"
          />

          <template v-else-if="step === 'folders'">
            <div
              v-if="!folderRows.length"
              class="text-center py-6 grey--text"
            >
              {{ $t("riskAssessment.templates.noTemplatesAvailable") }}
            </div>
            <v-list v-else>
              <v-list-item
                v-for="folder in folderRows"
                :key="folder.id"
                @click="openFolder(folder)"
              >
                <v-list-item-icon class="mr-4">
                  <v-icon color="primary">
                    {{ folder.isNone ? "mdi-folder-outline" : "mdi-folder" }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ folder.name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="flex-row align-center">
                  <span class="grey--text mr-2">{{ folder.templates.length }}</span>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </template>

          <v-list v-else-if="step === 'templates'">
            <v-list-item
              v-for="template in currentFolder.templates"
              :key="template._id"
              @click="openTemplate(template)"
            >
              <v-list-item-icon class="mr-4">
                <v-icon>mdi-file-document-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ template.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t("riskAssessment.templates.risksCount") }}:
                  {{ (template.risks || []).length }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <template v-else-if="step === 'risks' && selectedTemplate">
            <div class="d-flex mt-2">
              <v-btn small text color="primary" @click="selectAll">
                {{ $t("riskAssessment.templates.selectAll") }}
              </v-btn>
              <v-btn small text @click="selectedRisks = []">
                {{ $t("riskAssessment.templates.deselectAll") }}
              </v-btn>
            </div>
            <v-list dense>
              <v-list-item
                v-for="(risk, index) in selectedTemplate.risks"
                :key="index"
              >
                <v-list-item-action>
                  <v-checkbox
                    v-model="selectedRisks"
                    :value="index"
                    color="primary"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ risk.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    risk.action | stripHtml | truncate(80)
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialog = false">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            v-if="step === 'risks'"
            color="primary"
            :disabled="selectedRisks.length === 0"
            data-cy="addAreas"
            @click="appendRisks"
          >
            {{ $t("riskAssessment.templates.appendToEnd") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const RISK_FIELDS = [
  "stepTitle",
  "title",
  "riskClassification",
  "action",
  "riskClassificationAfterAction",
  "measurement",
  "legal",
  "relevantForPregnant",
  "actionForPregnant",
  "riskClassificationAfterActionForPregnant",
  "involvesThirdParties",
  "requiresSpecialTraining",
  "hazardFactor",
  "hazardFactors",
  "hazardPoints",
  "hazardDescription",
  "measureType",
  "measureTypeText",
  "stopMeasures",
  "furtherMeasures",
  "furtherMeasuresText",
  "riskClassificationAfterFurtherMeasures",
];

export default {
  props: ["value", "config"],
  filters: {
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    stripHtml(text) {
      if (!text) return "";
      return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    },
  },
  data: () => ({
    dialog: false,
    loading: false,
    step: "folders",
    templates: [],
    folders: [],
    currentFolder: null,
    selectedTemplate: null,
    selectedRisks: [],
  }),
  computed: {
    kind() {
      return this.config && this.config.kind === "advanced"
        ? "advanced"
        : "basic";
    },
    folderRows() {
      const byFolder = {};
      for (const template of this.templates) {
        const category = template.category;
        const key = category ? String(category._id || category) : "none";
        (byFolder[key] = byFolder[key] || []).push(template);
      }

      const rows = this.folders
        .map((folder) => ({
          id: String(folder._id),
          name: folder.name,
          isNone: false,
          templates: byFolder[String(folder._id)] || [],
        }))
        .filter((row) => row.templates.length)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );

      if (byFolder.none && byFolder.none.length) {
        rows.push({
          id: "none",
          name: this.$t("riskAssessment.templates.folder.ohneOrdner"),
          isNone: true,
          templates: byFolder.none,
        });
      }
      return rows;
    },
    dialogTitle() {
      if (this.step === "risks" && this.selectedTemplate) {
        return this.selectedTemplate.name;
      }
      if (this.step === "templates" && this.currentFolder) {
        return this.currentFolder.name;
      }
      return this.$t("riskAssessment.templates.selectTemplate");
    },
  },
  methods: {
    async openDialog() {
      this.dialog = true;
      this.loading = true;
      this.step = "folders";
      this.currentFolder = null;
      this.selectedTemplate = null;
      this.selectedRisks = [];
      try {
        const [templatesRes, foldersRes] = await Promise.all([
          this.$store.dispatch("getRiskAssessmentTemplates", {
            kind: this.kind,
          }),
          this.$store.dispatch("getRiskAssessmentTemplateCategories"),
        ]);
        this.templates = (templatesRes && templatesRes.data) || [];
        this.folders = (foldersRes && foldersRes.data) || [];
      } finally {
        this.loading = false;
      }
    },
    openFolder(folder) {
      this.currentFolder = folder;
      this.step = "templates";
    },
    openTemplate(template) {
      this.selectedTemplate = template;
      this.selectedRisks = [];
      this.step = "risks";
    },
    goBack() {
      if (this.step === "risks") {
        this.step = "templates";
        this.selectedTemplate = null;
        this.selectedRisks = [];
      } else {
        this.step = "folders";
        this.currentFolder = null;
      }
    },
    selectAll() {
      if (!this.selectedTemplate) return;
      this.selectedRisks = this.selectedTemplate.risks.map((_, i) => i);
    },
    appendRisks() {
      if (!this.selectedTemplate) return;

      [...this.selectedRisks]
        .sort((a, b) => a - b)
        .forEach((index) => {
          const templateRisk = this.selectedTemplate.risks[index];
          const risk = {
            legal: "",
            relevantForPregnant: false,
            actionForPregnant: "",
            riskClassificationAfterActionForPregnant: 0,
            involvesThirdParties: false,
            requiresSpecialTraining: false,
            hazardFactor: null,
            hazardFactors: [],
            hazardPoints: [],
            hazardDescription: "",
            measureType: "",
            measureTypeText: "",
            furtherMeasures: false,
            furtherMeasuresText: "",
            riskClassificationAfterFurtherMeasures: null,
          };
          for (const field of RISK_FIELDS) {
            const value = templateRisk[field];
            if (value !== undefined && value !== null) {
              risk[field] =
                typeof value === "object"
                  ? JSON.parse(JSON.stringify(value))
                  : value;
            }
          }
          this.value.risks.push({
            ...risk,
            responsible: "",
            responsibleUser: null,
            implementBy: null,
            effectiveFrom: null,
            ticket: null,
            dialogs: {
              classificationDialog: { open: false },
              afterActionDialog: { open: false },
            },
          });
        });

      this.selectedRisks = [];
      this.selectedTemplate = null;
      this.dialog = false;
    },
  },
};
</script>
